import type { PValidationRule } from '../../../types/types'
import type { PDateRangePickerModel } from '../../../components'
import { $t } from '../../i18n'

// Check if object for validation is type of "PTermPickerModel"
const isPTermPickerModel = (model: any): model is PDateRangePickerModel => {
  return model && typeof model === 'object' && 'from' in model && 'to' in model
}

const required: PValidationRule = (value: string | number | []) => {
  const errorMessage = $t('This value is required')

  // Check PTermInputField.vue
  if (isPTermPickerModel(value)) {
    return (value.from !== null && value.to !== null) || errorMessage
  }

  // Check if value is array if contain some value
  if (Array.isArray(value)) {
    return value.length > 0 || errorMessage
  }

  // Check if value is number
  if (typeof value === 'number' && value === 0) {
    return true
  }

  return !!value || errorMessage
}

const phoneNumber: PValidationRule = (value: string | null | undefined) => {
  if (value === null || value === undefined || value === '') {
    return true
  }

  const pattern: RegExp = /^[0-9]{6,10}$/

  return pattern.test(value) || $t('Please enter a valid phone number')
}

const email: PValidationRule = (value: string | null) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  return pattern.test(value) || $t('Please enter a valid email address')
}

const url: PValidationRule = (value: string | null) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^https:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/

  return pattern.test(value) || $t('Please enter a valid url address that starts with https://')
}

const urlUnsecured: PValidationRule = (value: string | null) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/

  return pattern.test(value) || $t('Please enter a valid url address')
}

const urlWithoutProtocol: PValidationRule = (value: string | null) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^(https:\/\/)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/

  return pattern.test(value) || $t('Please enter a valid url address')
}

const number: PValidationRule = (value: any) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /-?\b\d+(\.\d+)?\b/

  return pattern.test(value) || $t('Please enter a number')
}

const databaseDecimal: PValidationRule = (value: any) => {
  const min = -99999999.99,
    max = 99999999.99

  const numberValidation = number(value)

  if (numberValidation !== true) {
    return numberValidation
  }

  if (value < min || value > max) {
    return $t('Between {min} and {max}', { min: min, max: max })
  }

  return true
}

const positiveNumber: PValidationRule = (value: any) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^(?:[1-9]\d*(?:\.\d+)?|0\.[0-9]*[1-9])$/

  return pattern.test(value) || $t('Please enter a positive number')
}

const positiveNumberWithZero: PValidationRule = (value: any) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^(?:0|[1-9]\d*)(?:\.\d+)?$/

  return pattern.test(value) || $t('Please enter a positive number or zero')
}

const negativeNumber: PValidationRule = (value: any) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^-(?:[1-9]\d*(?:\.\d+)?|0\.[0-9]*[1-9])$/

  return pattern.test(value) || $t('Please enter a negative number')
}

const negativeNumberWithZero: PValidationRule = (value: any) => {
  if (value === null || value === '') {
    return true
  }

  const pattern: RegExp = /^-(?:0|[1-9]\d*)(?:\.\d+)?$/

  return pattern.test(value) || $t('Please enter a negative number or zero')
}

const numberInRange = (min: number, max: number): PValidationRule => {
  return (value: any) => {
    if (value === null || value === '') {
      return true
    }

    const numberValidation = number(value)

    if (numberValidation !== true) {
      return numberValidation
    }

    value = parseFloat(value)

    return (value >= min && value <= max) || $t('Between {min} and {max}', { min: min, max: max })
  }
}

const textLength = (max: number): PValidationRule => {
  return (value: any) => {
    if (value === null || value === undefined || value === '') {
      return true
    }

    return String(value).length <= max || $t('Maximum {max} characters', { max: max })
  }
}

const regex = (regex: RegExp, errorMessage: string): PValidationRule => {
  return (value: any) => {
    if (value === null || value === '') {
      return true
    }

    return regex.test(value) || errorMessage
  }
}

export default {
  required,
  phoneNumber,
  email,
  url,
  urlUnsecured,
  urlWithoutProtocol,
  number,
  databaseDecimal,
  positiveNumber,
  positiveNumberWithZero,
  negativeNumber,
  negativeNumberWithZero,
  numberInRange,
  textLength,
  regex,
}
